/* ==========================================================================
   AndroonBazar — product-form.js
   <ab-variant-picker>: option pills/swatches → resolves the variant, updates
   the hidden id, price, availability, URL, sticky bar and gallery image.
   <ab-gallery>: thumbnail click swaps the featured image; click to zoom.
   ========================================================================== */

(function () {
  'use strict';

  /* --- Variant picker ------------------------------------------------------ */

  class ABVariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variant-json]');
      if (!dataEl) return;
      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      this.form = this.closest('form') || document.querySelector(`#${this.dataset.formId}`);
      this.addEventListener('change', this.onChange.bind(this));
      this.onChange(); // sync initial state
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map((set) => {
        const checked = set.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === options[i])
      );

      this.updateLabels();
      this.updateVariant(variant);
    }

    updateLabels() {
      this.querySelectorAll('fieldset').forEach((set) => {
        const label = set.querySelector('[data-option-value]');
        const checked = set.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
    }

    updateVariant(variant) {
      const idInput = this.form && this.form.querySelector('input[name="id"]');
      const button = this.form && this.form.querySelector('[data-atc]');
      const buttonText = button && button.querySelector('[data-atc-text]');
      const strings = window.themeStrings || {};

      if (!variant) {
        if (button) button.setAttribute('disabled', '');
        if (buttonText) buttonText.textContent = strings.unavailable || 'Unavailable';
        return;
      }

      if (idInput) idInput.value = variant.id;

      // Price
      document.querySelectorAll('[data-price-current]').forEach((el) => {
        el.textContent = formatMoney(variant.price);
      });
      document.querySelectorAll('[data-price-compare]').forEach((el) => {
        if (variant.compare_at_price > variant.price) {
          el.textContent = formatMoney(variant.compare_at_price);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      // Availability
      if (button) {
        if (variant.available) {
          button.removeAttribute('disabled');
          if (buttonText) buttonText.textContent = strings.addToCart || 'Add to cart';
        } else {
          button.setAttribute('disabled', '');
          if (buttonText) buttonText.textContent = strings.soldOut || 'Sold out';
        }
      }

      // Gallery: jump to the variant's image
      if (variant.featured_media) {
        const gallery = document.querySelector('ab-gallery');
        if (gallery && typeof gallery.showMedia === 'function') {
          gallery.showMedia(variant.featured_media.id);
        }
      }

      // URL (keeps refresh + share on the right variant)
      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      this.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail: { variant } })
      );
    }
  }

  function formatMoney(cents) {
    if (window.AndroonBazar && window.AndroonBazar.formatMoney) {
      return window.AndroonBazar.formatMoney(cents);
    }
    return 'Rs.' + (cents / 100).toLocaleString('en-PK', { maximumFractionDigits: 0 });
  }

  if (!customElements.get('ab-variant-picker')) {
    customElements.define('ab-variant-picker', ABVariantPicker);
  }

  /* --- Gallery -------------------------------------------------------------- */

  class ABGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      if (!this.slides.length) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.showMedia(thumb.dataset.mediaId);
        });
      });

      // Click-to-zoom on the active slide
      this.addEventListener('click', (e) => {
        const slide = e.target.closest('[data-gallery-slide]');
        if (!slide || slide.hidden) return;
        const img = slide.querySelector('img');
        if (!img) return;
        this.toggleZoom(slide, img, e);
      });

      this.showMedia(this.slides[0].dataset.mediaId, true);
    }

    showMedia(id, initial) {
      if (!id) return;
      let found = false;
      this.slides.forEach((slide) => {
        const match = String(slide.dataset.mediaId) === String(id);
        slide.hidden = !match;
        if (match) found = true;
      });
      if (!found) {
        this.slides.forEach((slide, i) => (slide.hidden = i !== 0));
      }
      this.thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === String(id);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (!initial) this.querySelector('[data-gallery-slide]:not([hidden]) img');
    }

    toggleZoom(slide, img, event) {
      const zoomed = slide.classList.toggle('is-zoomed');
      if (zoomed) {
        const rect = slide.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
        slide.setAttribute('aria-label', 'Zoomed. Click to reset.');
      } else {
        img.style.transformOrigin = '';
        slide.removeAttribute('aria-label');
      }
    }
  }

  if (!customElements.get('ab-gallery')) customElements.define('ab-gallery', ABGallery);

  /* --- Sticky mobile add-to-cart -------------------------------------------- */

  class ABStickyATC extends HTMLElement {
    connectedCallback() {
      this.target = document.querySelector('[data-atc]');
      if (!this.target || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(this.target);

      const trigger = this.querySelector('[data-sticky-atc]');
      if (trigger) {
        trigger.addEventListener('click', () => {
          const form = this.target.closest('form');
          if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
        });
      }

      document.addEventListener('variant:change', (e) => {
        const priceEl = this.querySelector('[data-sticky-price]');
        if (priceEl && e.detail.variant) priceEl.textContent = formatMoney(e.detail.variant.price);
        if (trigger) trigger.toggleAttribute('disabled', !e.detail.variant.available);
      });
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('ab-sticky-atc')) customElements.define('ab-sticky-atc', ABStickyATC);

  /* --- Recently viewed (records on product pages) ---------------------------- */

  try {
    const el = document.querySelector('[data-product-handle]');
    if (el) {
      const KEY = 'ab:recently-viewed';
      const handle = el.dataset.productHandle;
      const list = JSON.parse(localStorage.getItem(KEY) || '[]').filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, 12)));
    }
  } catch (e) {}
})();
